import { fetchApi } from '@/app/api';
import { TicketFilter } from '@/features/tickets/tickets.hooks';
import {
  DTO,
  parseTicketDto,
  parseTicketWithEventsDto,
  Ticket,
  TicketEvent,
  TicketWithEvents,
} from '@certhon/models';
import { map } from 'remeda';

const EVENTS_SELECT = `*,events:ticketevents(id,type,payload,meta,user_id,created,inserted)`;

/*
 * The ticket read RPCs (get_ticket_references, get_tickets_for_same_client,
 * get_tickets_for_project) are retired in favor of plain PostgREST queries
 * over CRM-chain denormalized columns — cacheable and emulatable offline by
 * the service worker. Subject-derived queries (references of / same client
 * as a loaded ticket) take their inputs from the caller's already-loaded
 * ticket instead of re-fetching it.
 */

export async function fetchTickets(qry?: TicketFilter) {
  const tickets = await fetchApi('/tickets', qry);
  return tickets.map(parseTicketDto);
}

/**
 * In- and outbound references of a ticket (was get_ticket_references).
 * `outboundIds` = the subject ticket's own ticket_references column.
 */
export async function fetchReferencedTickets(
  ticketId: string,
  outboundIds: string[],
) {
  const [outbound, inbound] = await Promise.all([
    outboundIds.length
      ? fetchApi('/tickets', {
          id: `in.(${outboundIds.join(',')})`,
          select: EVENTS_SELECT,
        })
      : Promise.resolve([]),
    fetchApi('/tickets', {
      ticket_references: `cs.{${ticketId}}`,
      select: EVENTS_SELECT,
    }),
  ]);
  const byId = new Map(
    [...outbound, ...inbound].map((t) => [(t as DTO<Ticket>).id, t]),
  );
  return [...byId.values()].map((t) =>
    parseTicketWithEventsDto(t as DTO<TicketWithEvents>),
  );
}

export async function fetchTicket(ticketId: string) {
  const results = await fetchApi('/tickets', {
    id: `eq.${ticketId}`,
    select: EVENTS_SELECT,
  });
  if (!results.length) {
    throw new Error('Could not find Ticket');
  }
  return parseTicketWithEventsDto(results[0] as DTO<TicketWithEvents>);
}

export function postTicketEvents(newEvents: TicketEvent[]) {
  return fetchApi('/ticketevents', undefined, {
    method: 'POST',
    body: JSON.stringify(newEvents),
  }).then(map(parseTicketWithEventsDto));
}
